import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  LinearProgress,
  Button,
} from '@mui/material';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import Client from './../Client';
import BackofficeReportsApi from './backofficeReportsApi';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatMoney(value) {
  const numberValue = Number(value || 0);

  if (numberValue >= 1000000) {
    return thousandsSeparator((numberValue / 1000000).toFixed(1)) + "M";
  }

  if (numberValue >= 1000) {
    return thousandsSeparator((numberValue / 1000).toFixed(1)) + "K";
  }

  return thousandsSeparator(numberValue.toFixed(0));
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

class PromotionsPerformanceByPromotion extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client('none'),
      data: [],
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.periodMode !== this.props.periodMode) {
      this.loadData();
    }
  }

  loadData = () => {
    const periodMode = this.props.periodMode || 'last_30d';

    let queryData = {
      periodMode: periodMode,
    };

    let data = BackofficeReportsApi.getPromotionsPerformance(this.state.client, queryData);

    if (data) {
      this.setState({
        data: data.rows || data || [],
      });
    }
  };

  csvValue = (value) => {
    if (value === null || typeof value === 'undefined') return '""';

    const cleanValue = String(value).replace(/"/g, '""');

    return `"${cleanValue}"`;
  };

  handleExportCsv = () => {
    const rows = this.state.data || [];

    if (!rows.length) {
      return;
    }

    const periodMode = this.props.periodMode || 'last_30d';

    const totalClaimsCreated = rows.reduce(
      (sum, row) => sum + Number(row.claims_created || 0),
      0
    );

    const totalClaimsConsumed = rows.reduce(
      (sum, row) => sum + Number(row.claims_consumed || 0),
      0
    );

    const totalOrdersWithClaims = rows.reduce(
      (sum, row) => sum + Number(row.orders_with_claims || 0),
      0
    );

    const totalSalesFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.sales_from_claims || 0),
      0
    );

    const totalDiscountFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.discount_from_claims || 0),
      0
    );

    const weightedClaimConversion = totalClaimsCreated > 0
      ? (totalClaimsConsumed / totalClaimsCreated) * 100
      : 0;

    const weightedDiscountRate = totalSalesFromClaims > 0
      ? (totalDiscountFromClaims / totalSalesFromClaims) * 100
      : 0;

    const averageDiscountPerUsedClaim = totalClaimsConsumed > 0
      ? totalDiscountFromClaims / totalClaimsConsumed
      : 0;

    const highCostPromotions = rows.filter(
      (row) => Number(row.discount_rate || 0) > 15
    ).length;

    const headers = [
      { key: 'promotion_name', label: 'Promotion' },
      { key: 'claims_created', label: 'Claims' },
      { key: 'claims_consumed', label: 'Used' },
      { key: 'claim_conversion', label: 'Usage %' },
      { key: 'orders_with_claims', label: 'Orders' },
      { key: 'sales_from_claims', label: 'Influenced sales' },
      { key: 'discount_from_claims', label: 'Discount' },
      { key: 'discount_rate', label: 'Cost %' },
      { key: 'average_discount', label: 'Avg. discount' },
    ];

    const csvRows = [
      ['Promotion performance'].map((value) => this.csvValue(value)).join(','),
      ['Period', periodMode].map((value) => this.csvValue(value)).join(','),
      ['Generated at', new Date().toLocaleString()].map((value) => this.csvValue(value)).join(','),
      '',
      ['Summary'].map((value) => this.csvValue(value)).join(','),
      ['Total claims', totalClaimsCreated].map((value) => this.csvValue(value)).join(','),
      ['Total used claims', totalClaimsConsumed].map((value) => this.csvValue(value)).join(','),
      ['Weighted usage %', `${weightedClaimConversion.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Orders with claims', totalOrdersWithClaims].map((value) => this.csvValue(value)).join(','),
      ['Total influenced sales', totalSalesFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Total discount', totalDiscountFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Weighted cost %', `${weightedDiscountRate.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Average discount per used claim', averageDiscountPerUsedClaim.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Promotions with cost % over 15%', highCostPromotions].map((value) => this.csvValue(value)).join(','),
      '',
      headers.map((header) => this.csvValue(header.label)).join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            if (header.key === 'claim_conversion' || header.key === 'discount_rate') {
              return this.csvValue(`${Number(row[header.key] || 0).toFixed(1)}%`);
            }

            if (
              header.key === 'sales_from_claims' ||
              header.key === 'discount_from_claims' ||
              header.key === 'average_discount'
            ) {
              return this.csvValue(Number(row[header.key] || 0).toFixed(2));
            }

            return this.csvValue(row[header.key]);
          })
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `promotion_performance_${periodMode}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.state.data || [];

    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.25, sm: 2 },
            '&:last-child': { pb: { xs: 1.25, sm: 2 } },
          }}
        >
          <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: '#111827',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Promotion performance
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    mt: 0.25,
                    fontSize: { xs: '0.74rem', sm: '0.82rem' },
                    lineHeight: 1.25,
                  }}
                >
                  Claims, usage rate, influenced sales and discount cost by promotion
                </Typography>
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadRoundedIcon />}
                disabled={!rows.length}
                onClick={this.handleExportCsv}
                sx={{
                  flexShrink: 0,
                  height: 32,
                  minWidth: 64,
                  px: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: { xs: '0.68rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap',
                  color: '#111827',
                  borderColor: 'rgba(15, 23, 42, 0.14)',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: 'rgba(15, 23, 42, 0.24)',
                  },
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                    ml: 0,
                  },
                }}
              >
                CSV
              </Button>
            </Stack>

            <TableContainer
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: 'auto',
                borderRadius: '12px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: { xs: 1040, md: 1160 },
                  '& .MuiTableCell-root': {
                    fontSize: { xs: '0.7rem', sm: '0.78rem' },
                    py: { xs: 0.7, sm: 0.9 },
                    px: { xs: 0.75, sm: 1 },
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 800,
                    color: '#1a237e',
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Promotion</TableCell>
                    <TableCell align="right">Claims</TableCell>
                    <TableCell align="right">Used</TableCell>
                    <TableCell align="right">Usage</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">Influenced sales</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Avg.</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => {
                    const claimConversion = Number(row.claim_conversion || 0);
                    const discountRate = Number(row.discount_rate || 0);

                    return (
                      <TableRow key={`${row.promotion_iref || 'promo'}-${index}`} hover>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: '#111827',
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.promotion_name || 'Unknown'}
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.claims_created || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.claims_consumed || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right" sx={{ minWidth: 120 }}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                            <Box sx={{ width: 54 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(claimConversion, 100)}
                                sx={{
                                  height: 6,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(26, 35, 126, 0.08)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 999,
                                    bgcolor: '#1a237e',
                                  },
                                }}
                              />
                            </Box>

                            <Box sx={{ minWidth: 42, fontWeight: 800 }}>
                              {formatPercent(claimConversion)}
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.orders_with_claims || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.sales_from_claims)}
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.discount_from_claims)}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 800,
                            color: discountRate > 15 ? '#b91c1c' : '#047857',
                          }}
                        >
                          {formatPercent(discountRate)}
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.average_discount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No promotion performance data available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default PromotionsPerformanceByPromotion;
